import React, { useEffect, useState } from 'react';
import { Edit2, Save, X, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Format date as "02 May 2026 (2 years ago)"
function formatDateWithRelative(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  // Format: "02 May 2026"
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  const formatted = date.toLocaleDateString('en-US', options);

  // Calculate relative time
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let relative = '';
  if (diffDays < 1) {
    relative = 'today';
  } else if (diffDays < 7) {
    relative = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    relative = `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    relative = `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    relative = `${years} year${years > 1 ? 's' : ''} ago`;
  }

  return `${formatted} (${relative})`;
}

interface CompanyData {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  createdAt: string;
}

export default function SettingsCompany() {
  const { user: currentUser } = useAuth();
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  // Fetch company data on mount
  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await api.get<any>('/company');
      if (response.company) {
        setCompany(response.company);
        setCanEdit(response.canEdit);
        setFormData({
          name: response.company.name || '',
          email: response.company.email || '',
          phone: response.company.phone || '',
          address: response.company.address || '',
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch company data:', err);
      setError(err.message || 'Failed to load company information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setError('');
      setIsSaving(true);

      // Validate required fields
      if (!formData.name.trim()) {
        setError('Company name is required');
        return;
      }
      if (!formData.email.trim()) {
        setError('Company email is required');
        return;
      }

      const response = await api.put<any>('/company', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        address: formData.address || null,
      });

      if (response.success && response.company) {
        setCompany(response.company);
        setIsEditing(false);
      }
    } catch (err: any) {
      console.error('Failed to save company data:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
      });
    }
    setIsEditing(false);
    setError('');
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Company Settings</h1>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-blue-800">Company information not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Settings</h1>
          <p className="text-gray-500 mt-1">Manage your company information</p>
        </div>
        {canEdit && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
          >
            <Edit2 className="h-4 w-4" />
            Edit
          </button>
        )}
      </div>

      {/* Permission Message */}
      {!canEdit && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Only company owners can edit company settings. Contact your company owner to make changes.
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Company Details Card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-900">Company Information</h2>
        </div>

        <div className="p-6">
          {isEditing ? (
            // Edit Mode
            <div className="space-y-5">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                  placeholder="Enter company name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                  placeholder="Enter company email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                  placeholder="Enter company phone"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                  placeholder="Enter company address"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-5">
              {/* Company Name */}
              <div className="pb-5 border-b border-gray-200">
                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Company Name</p>
                <p className="text-gray-900">{company.name}</p>
              </div>

              {/* Email */}
              <div className="pb-5 border-b border-gray-200">
                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Email</p>
                <p className="text-gray-900">{company.email}</p>
              </div>

              {/* Phone */}
              <div className="pb-5 border-b border-gray-200">
                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Phone</p>
                <p className="text-gray-900">{company.phone || '-'}</p>
              </div>

              {/* Address */}
              <div className="pb-5 border-b border-gray-200">
                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Address</p>
                <p className="text-gray-900 whitespace-pre-wrap">{company.address || '-'}</p>
              </div>

              {/* Subscription Status */}
              <div className="pb-5 border-b border-gray-200">
                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Subscription Status</p>
                <div className="flex items-center gap-2">
                  <p className="text-gray-900 capitalize">{company.subscriptionStatus}</p>
                  {company.subscriptionPlan && (
                    <span className="px-2 py-1 text-xs font-medium bg-cyan-100 text-cyan-700 rounded">
                      {company.subscriptionPlan}
                    </span>
                  )}
                </div>
              </div>

              {/* Created Date */}
              <div>
                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Created</p>
                <p className="text-gray-900">
                  {formatDateWithRelative(company.createdAt)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
